import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global search bar for the admin top nav.
 *
 * Fires `/api/search?q=…` with 250ms debounce; renders a dropdown of
 * cohorts / learners / tracks / LEXPs (tenant-scoped on the backend).
 * Picking a result navigates to the matching detail page. Styling pulls
 * from per-theme CSS vars (`--topnav-search-bg`, `--topnav-search-border`)
 * so the search bar adapts to each theme's aesthetic.
 */
export default function TopNavSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search — cancel on each keystroke, only fire after 250ms idle.
  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q: q.trim(), limit: 6 }, withCredentials: true });
        setResults(data);
      } catch {
        setResults({ error: true, results: [] });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    const h = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (r) => {
    setOpen(false);
    setQ("");
    navigate(r.href);
  };

  const hasAny = results && results.results && results.results.length > 0;

  return (
    <div ref={boxRef} className="relative hidden md:block" style={{ width: "300px" }} data-testid="topnav-search">
      <div
        className="flex items-center gap-2 h-9 px-4 transition-colors"
        style={{
          backgroundColor: "var(--topnav-search-bg, rgba(255,255,255,0.12))",
          border: "1px solid var(--topnav-search-border, rgba(255,255,255,0.2))",
          borderRadius: "20px",          // pill
          boxShadow: "none",
          color: "var(--shell-text)",
        }}
      >
        <Search className="h-4 w-4 shrink-0" style={{ color: "var(--topnav-search-placeholder, rgba(255,255,255,0.6))" }} />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search learners, cohorts, tracks, LEXPs…"
          data-testid="topnav-search-input"
          className="bg-transparent outline-none flex-1 text-sm font-body topnav-search-input"
          style={{ color: "var(--shell-text)", boxShadow: "none" }}
        />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--topnav-search-placeholder, rgba(255,255,255,0.6))" }} />}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-lg shadow-xl overflow-hidden z-50"
          style={{
            backgroundColor: "var(--admin-container-bg, #FFFFFF)",
            border: "1px solid var(--admin-container-border)",
            color: "var(--admin-container-text)",
          }}
          data-testid="topnav-search-dropdown"
        >
          {!loading && !hasAny && (
            <div className="px-4 py-6 text-sm font-body text-center" style={{ color: "var(--admin-container-muted)" }} data-testid="topnav-search-empty">
              No results for "{q}".
            </div>
          )}
          {hasAny && (
            <div className="max-h-96 overflow-y-auto">
              {results.results.map((r) => (
                <button
                  key={`${r.type}:${r.id}`}
                  onClick={() => go(r)}
                  data-testid={`topnav-search-result-${r.type}`}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-sm font-body hover:bg-black/5 transition-colors"
                  style={{ color: "var(--admin-container-text)" }}
                >
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: "var(--shell-section-label)", color: "var(--on-secondary)" }}
                  >
                    {r.type}
                  </span>
                  <span className="truncate flex-1">{r.label}</span>
                  {r.subtitle && (
                    <span className="text-xs opacity-60 truncate max-w-[140px]">{r.subtitle}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
